// `/super-admin/claim#token=` (SUPER-ADMIN): where the host command `ops:super-admin-link` sends
// the person it names. A public route, outside the auth guard. The token rides the URL fragment
// (never sent to a server, never in a log or a Referer) and is read once into memory.
//   - signed out: "Create my login" (the person's own name and password: `/v1/auth/sign-up/email`)
//     or "I already have an account" (`/v1/auth/sign-in/email`); then the claim;
//   - signed in: "Become super admin" (`POST /v1/super-admin/claim`);
//   - then two-step sign-in is set up from Account (required for every agency user) and the
//     operator workspace opens.
// Nothing here sets or sees a password other than the one the person types.
import { ApiError } from "@companyos/sdk";
import {
  Button,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Notice,
  Segmented,
  Spinner,
} from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createRoute, useRouter } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import type { MeResponse } from "../api/types";
import { AuthFrame } from "../features/auth/auth-frame";
import { rootRoute } from "./__root";

const TOKEN = /^cossa_[A-Za-z0-9_-]{43}$/;

export const superAdminClaimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "super-admin/claim",
  component: SuperAdminClaimPage,
});

/** The token from `#token=…`, then the fragment is cleared from the address bar. */
function takeToken(): string {
  if (typeof window === "undefined") return "";
  const m = /(?:^#|&)token=([^&]+)/.exec(window.location.hash);
  const token = m?.[1] ? decodeURIComponent(m[1]) : "";
  if (window.location.hash)
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return token;
}

type Session = { state: "loading" } | { state: "out" } | { state: "in"; me: MeResponse };

function messageOf(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404)
      return "This link is unknown, used or expired, or it was issued for another address. Sign in with that address, or run the command again for a new link.";
    if (e.status === 409) return e.problem?.detail ?? "This account cannot become a super admin.";
    return e.problem?.detail ?? e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong. Try again.";
}

function SuperAdminClaimPage() {
  const { api } = superAdminClaimRoute.useRouteContext();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [token] = useState(takeToken);
  const [session, setSession] = useState<Session>({ state: "loading" });
  const [mode, setMode] = useState<"create" | "existing">("create");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const readSession = useCallback(async () => {
    try {
      setSession({ state: "in", me: (await api.client.me.get()) as MeResponse });
    } catch {
      setSession({ state: "out" });
    }
  }, [api]);
  useEffect(() => {
    void readSession();
  }, [readSession]);

  async function claim() {
    setPending(true);
    setError(null);
    try {
      await api.client.superAdmin.claim({ body: { token } });
      setDone(true);
      queryClient.removeQueries({ queryKey: ["me"] });
      await router.invalidate();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setPending(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (mode === "create") await api.auth.signUp({ email, password, name });
      else {
        const step = await api.auth.signIn(email, password);
        if (step.twoFactor) {
          setError(
            "This account uses two-step sign-in: sign in from the sign-in page, then open the link again.",
          );
          return;
        }
      }
      await readSession();
      await claim();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setPending(false);
    }
  }

  if (!TOKEN.test(token))
    return (
      <AuthFrame title="Become super admin">
        <Notice tone="danger" role="alert" title="This link is incomplete">
          Open the whole link the command printed (it ends with <code>#token=…</code>), or run the
          command again for a new one.
        </Notice>
      </AuthFrame>
    );

  if (done)
    return (
      <AuthFrame
        title="You are a super admin"
        description="One more step: every operator account uses two-step sign-in. Set it up now, then open the operator workspace."
      >
        <Button size="lg" onClick={() => void router.navigate({ to: "/account" })}>
          Set up two-step sign-in
        </Button>
      </AuthFrame>
    );

  if (session.state === "loading")
    return (
      <AuthFrame title="Become super admin">
        <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Checking your session…
        </div>
      </AuthFrame>
    );

  if (session.state === "in")
    return (
      <AuthFrame
        title="Become super admin"
        description={
          <>
            Signed in as{" "}
            <span className="font-medium text-foreground">{session.me.user.email}</span>. The link
            works once, for the address it was issued for.
          </>
        }
      >
        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <Button size="lg" disabled={pending} onClick={() => void claim()}>
          {pending ? <Spinner /> : null}
          Become super admin
        </Button>
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="Become super admin"
      description="Create the login you will run the platform with: your own address and a password only you know. The link works once."
    >
      <Segmented
        aria-label="Account"
        value={mode}
        onValueChange={(v) => setMode(v as "create" | "existing")}
        options={[
          { value: "create", label: "Create my login" },
          { value: "existing", label: "I already have an account" },
        ]}
      />
      <form onSubmit={onSubmit} noValidate>
        <FieldGroup className="gap-4">
          {mode === "create" ? (
            <Field>
              <FieldLabel htmlFor="name">Name</FieldLabel>
              <Input
                id="name"
                autoComplete="name"
                required
                className="h-10"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor="email">Email (the address the link was issued for)</FieldLabel>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              className="h-10"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Input
              id="password"
              type="password"
              autoComplete={mode === "create" ? "new-password" : "current-password"}
              required
              className="h-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          <Button type="submit" size="lg" disabled={pending}>
            {pending ? <Spinner /> : null}
            {mode === "create"
              ? "Create my login and become super admin"
              : "Sign in and become super admin"}
          </Button>
        </FieldGroup>
      </form>
    </AuthFrame>
  );
}
